<template>
  <div class="flex flex-col space-y-4">
    <div class="flex">
      <span class="text-gray-700 font-medium py-2 sm:text-sm">{{
        t("admin.tasks.recurring.repeat")
      }}</span>
      <frequency-component
        @change="handleFrequencyChanged"
        class="ml-3"
        :value="frequency"
        :disabled="disabled"
      />
    </div>

    <div v-if="isDaily">
      <Daily @change="handleChange" :interval="interval" :disabled="disabled" />
    </div>

    <div v-if="isWeekly">
      <Weekly
        @change="handleChange"
        :input-interval="interval"
        :input-days="weekDays"
        :disabled="disabled"
      />
    </div>

    <div v-if="isMonthly">
      <Monthly
        @change="handleChange"
        :input-interval="interval"
        :input-on-day-or-on-selection="onDayOrOnSelection"
        :input-n-week-day="nWeekDays[0]"
        :input-date="getDate"
        :disabled="disabled"
      />
    </div>

    <div v-if="isYearly">
      <Yearly
        @change="handleChange"
        :input-interval="interval"
        :input-on-day-or-on-selection="onDayOrOnSelection"
        :input-n-week-day="nWeekDays[0]"
        :input-month="getMonth"
        :input-date="getDate"
        :disabled="disabled"
      />
    </div>
  </div>
</template>

<script setup>
import { RRule } from "rrule";
import Daily from "@/Tailwind/Components/RRule/Daily";
import Weekly from "@/Tailwind/Components/RRule/Weekly";
import Monthly from "@/Tailwind/Components/RRule/Monthly";
import Yearly from "@/Tailwind/Components/RRule/Yearly";
import FrequencyComponent from "@/Tailwind/Components/RRule/Components/Frequency";

import { useI18n } from "vue-i18n";
import { computed, defineEmits, defineProps, ref } from "vue";

const { t } = useI18n();

const emit = defineEmits(["change"]);

const props = defineProps({
  rule: RRule,
  disabled: Boolean,
});

const frequency = ref(props.rule ? props.rule.options.freq : RRule.DAILY);
const interval = ref(props.rule ? props.rule.options.interval : 1);
const weekDays = ref(props.rule ? getWeekDay() : [RRule.MO]);
const nWeekDays = ref(props.rule ? getNWeekDay() : [RRule.MO]);

const isDaily = computed(() => {
  return frequency.value.valueOf() === RRule.DAILY.valueOf();
});

const isWeekly = computed(() => {
  return frequency.value.valueOf() === RRule.WEEKLY.valueOf();
});

const isMonthly = computed(() => {
  return frequency.value.valueOf() === RRule.MONTHLY.valueOf();
});

const isYearly = computed(() => {
  return frequency.value.valueOf() === RRule.YEARLY.valueOf();
});

const getDate = computed(() => {
  if (props.rule?.options.bymonthday) {
    return props.rule.options.bymonthday[0];
  }

  if (props.rule?.options.bymonthday) {
    return props.rule.options.bymonthday[0];
  }

  return 1;
});

const onDayOrOnSelection = computed(() => {
  if (
    !props.rule?.options.bynweekday &&
    !props.rule?.options.bymonthday &&
    !props.rule?.options.bymonth
  ) {
    return 0;
  }

  if (
    props.rule?.options.bynweekday &&
    props.rule?.options.bynweekday.length > 0
  ) {
    return 1;
  }

  return 0;
});

const getMonth = computed(() => {
  if (!props.rule?.options.bymonth) {
    return 0;
  }

  return props.rule?.options.bymonth[0];
});

function handleFrequencyChanged(v) {
  frequency.value = v;
}

function handleChange(rule) {
  emit("change", rule);
}

function getWeekDay() {
  let weekDays = [];

  if (!props.rule.options.byweekday) {
    return [RRule.MO];
  }

  props.rule.options.byweekday.forEach((weekDay) => {
    switch (weekDay) {
      case 0:
        weekDays.push(RRule.MO);
        break;
      case 1:
        weekDays.push(RRule.TU);
        break;
      case 2:
        weekDays.push(RRule.WE);
        break;
      case 3:
        weekDays.push(RRule.TH);
        break;
      case 4:
        weekDays.push(RRule.FR);
        break;
      case 5:
        weekDays.push(RRule.SA);
        break;
      case 6:
        weekDays.push(RRule.SU);
        break;
      default:
        weekDays.push(RRule.MO);
    }
  });

  return weekDays;
}

function getNWeekDay() {
  let nWeekdays = [];

  if (!props.rule.options.bynweekday) {
    return [
      {
        weekDay: RRule.MO,
        n: 1,
      },
    ];
  }

  props.rule.options.bynweekday.forEach((nWeekDay) => {
    switch (nWeekDay[0]) {
      case 0:
        nWeekdays.push({
          weekDay: RRule.MO,
          n: nWeekDay[1],
        });
        break;
      case 1:
        nWeekdays.push({
          weekDay: RRule.TU,
          n: nWeekDay[1],
        });
        break;
      case 2:
        nWeekdays.push({
          weekDay: RRule.WE,
          n: nWeekDay[1],
        });
        break;
      case 3:
        nWeekdays.push({
          weekDay: RRule.TH,
          n: nWeekDay[1],
        });
        break;
      case 4:
        nWeekdays.push({
          weekDay: RRule.FR,
          n: nWeekDay[1],
        });
        break;
      case 5:
        nWeekdays.push({
          weekDay: RRule.SA,
          n: nWeekDay[1],
        });
        break;
      case 6:
        nWeekdays.push({
          weekDay: RRule.SU,
          n: nWeekDay[1],
        });
        break;
      default:
        nWeekdays.push({
          weekDay: RRule.MO,
          n: nWeekDay[1],
        });
    }
  });

  return nWeekdays;
}
</script>
